/**
 * @fileoverview React Query hooks for Admin Vendors data fetching
 *
 * Provides hooks for fetching and managing vendor data in the admin portal.
 * Uses React Query for caching, background updates, and request deduplication.
 *
 * @requires react-query
 * @requires firebase/functions
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions();

/**
 * Fetch all vendors for admin view with optional filtering
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by vendor status
 * @param {string} options.vendorCategory - Filter by vendor category
 * @param {string} options.hasShowroom - Filter by showroom availability
 * @param {string} options.isClientFacing - Filter by client-facing status
 * @returns {Promise<Array>} Array of vendor objects
 */
const fetchAllVendorsForAdmin = async (options = {}) => {
  const getAllVendorsForAdmin = httpsCallable(
    functions,
    "getAllVendorsForAdmin",
  );
  const result = await getAllVendorsForAdmin(options);
  return result.data.data;
};

/**
 * Fetch single vendor by ID
 * @param {string} vendorId - Vendor record ID
 * @returns {Promise<Object>} Vendor object
 */
const fetchVendorById = async (vendorId, { showTestAccounts } = {}) => {
  const getVendorById = httpsCallable(functions, "getVendorByIdAdmin");
  const result = await getVendorById({ vendorId, showTestAccounts });
  return result.data.data;
};

/**
 * React Query hook for fetching all vendors with filtering
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by vendor status
 * @param {string} options.vendorCategory - Filter by vendor category
 * @param {string} options.hasShowroom - Filter by showroom availability
 * @param {string} options.isClientFacing - Filter by client-facing status
 * @returns {Object} React Query result object
 */
export const useAdminVendors = (options = {}) => {
  return useQuery({
    queryKey: ["adminVendors", options],
    queryFn: () => fetchAllVendorsForAdmin(options),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * React Query hook for fetching single vendor by ID
 * @param {string} vendorId - Vendor record ID
 * @param {Object} options - React Query options
 * @returns {Object} React Query result object
 */
export const useAdminVendor = (vendorId, { showTestAccounts, ...options } = {}) => {
  return useQuery({
    queryKey: ["adminVendor", vendorId, showTestAccounts],
    queryFn: () => fetchVendorById(vendorId, { showTestAccounts }),
    enabled: !!vendorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Fetch vendor category options from the Meta API
 * @returns {Promise<Array<string>>} Array of category name strings
 */
const fetchVendorCategories = async () => {
  const getVendorCategories = httpsCallable(functions, "getVendorCategories");
  const result = await getVendorCategories();
  return result.data?.data || result.data || [];
};

/**
 * React Query hook for fetching vendor category options
 *
 * Uses the Airtable Meta API to return the configured choices for the
 * Vendor Category Multiple Select field. Cached for 30 minutes since
 * field options rarely change.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<string>>}
 */
export const useVendorCategories = () => {
  return useQuery({
    queryKey: ["vendorCategories"],
    queryFn: fetchVendorCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for creating a new vendor
 * @return {Object} React Query mutation object
 */
export const useCreateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorData) => {
      const createVendorFn = httpsCallable(functions, "createVendor");
      const result = await createVendorFn(vendorData);
      return result.data;
    },
    onSuccess: () => {
      // Invalidate vendors list to refresh
      queryClient.invalidateQueries({ queryKey: ["adminVendors"] });
    },
    onError: (error) => {
      console.error("[useCreateVendor] Error:", error);
    },
  });
};

/**
 * Hook for updating an existing vendor
 * @return {Object} React Query mutation object
 */
export const useUpdateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ vendorId, updates }) => {
      const updateVendorFn = httpsCallable(functions, "updateVendor");
      const result = await updateVendorFn({ vendorId, updates });
      return result.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate both the list and the individual vendor
      queryClient.invalidateQueries({ queryKey: ["adminVendors"] });
      queryClient.invalidateQueries({
        queryKey: ["adminVendor", variables.vendorId],
      });
    },
    onError: (error) => {
      console.error("[useUpdateVendor] Error:", error);
    },
  });
};
